import type {
  DirectoryFilters,
  DirectoryOpportunity,
  NSFAward,
  REUSite,
  ResearchData,
  ResearchOpportunitiesDirectoryViewModel,
} from './types';

export const ITEMS_PER_PAGE = 50;
export const DIRECTORY_PAGE_SIZE = 20;

export const DASHBOARD_TAG_QUERIES: Record<string, string> = {
  cs: 'computer science artificial intelligence machine learning',
  bio: 'biology life sciences',
  physics: 'physics engineering',
  social: 'social sciences psychology sociology economics policy',
};

const isRutgersREU = (project: ResearchData['projects'][number]) => (
  /REU/i.test([
    project.title,
    project.program_type,
    project.summary,
    project.full_description,
    project.mentoring_plan,
  ].join(' '))
);

export function toOpportunities(
  data: ResearchData | null,
  awards: NSFAward[],
  sites: REUSite[],
  filters: DirectoryFilters,
): DirectoryOpportunity[] {
  const rutgers = (data?.projects ?? []).map((project) => ({
    id: `rutgers-${project.project_id}`,
    kind: 'rutgers' as const,
    title: project.title,
    professor: project.professor,
    department: project.department,
    programType: project.program_type,
    subjects: [...project.subjects, ...project.subject_headings],
    summary: project.summary,
    campus: project.project_details.campus ?? '',
    compensation: project.project_details.compensation,
    concluded: project.is_concluded,
    rutgersREU: isRutgersREU(project),
    project,
  }));

  const nsf = filters.includeNSF
    ? awards.map((award) => ({
        id: `nsf-${award.id}`,
        kind: 'nsf-award' as const,
        title: award.title,
        professor: award.pdPIName || 'Not listed',
        department: award.fundProgramName || 'NSF',
        programType: award.program || 'NSF Grant',
        subjects: [award.fundProgramName].filter(Boolean),
        summary: '',
        campus: award.perfLocation || '',
        concluded: !award.activeAwd,
        award,
      }))
    : [];

  const reu = filters.includeREUSites
    ? sites.map((site) => ({
        id: `reu-${site.id}`,
        kind: 'reu-site' as const,
        title: site.title,
        professor: site.primary_contact_name || 'Not listed',
        department: site.institution_department || 'REU',
        programType: 'NSF REU Site',
        subjects: [
          site.institution_department,
          site.research_areas,
          site.research_topics_keywords,
        ].filter(Boolean) as string[],
        summary: site.site_description || '',
        campus: site.institution_city || site.site_location || '',
        concluded: false,
        site,
      }))
    : [];

  return [...rutgers, ...nsf, ...reu];
}

function includesQuery(values: Array<string | undefined>, query: string) {
  return values.some((value) => value?.toLocaleLowerCase().includes(query));
}

export function compensationSignals(value = '') {
  const text = value.toLowerCase();
  return {
    paid: !/unpaid|no pay|not paid|without pay/.test(text) && /\bpaid\b|pay|stipend|salary|hourly|\$/.test(text),
    credit: !/no credit|not for credit|without credit/.test(text) && /credit/.test(text),
  };
}

function matchesRutgersFilters(item: DirectoryOpportunity, filters: DirectoryFilters) {
  const project = item.project!;
  const query = filters.searchQuery.trim().toLocaleLowerCase();
  const hasCompensation = Boolean(
    project.project_details.compensation
      && project.project_details.compensation !== 'N/A'
      && project.project_details.compensation.trim(),
  );

  return (
    (filters.includeConcluded || !project.is_concluded)
    && (filters.includeREU || !item.rutgersREU)
    && (!query || includesQuery([
      project.title,
      project.professor,
      project.department,
      project.summary,
      project.full_description,
      ...project.subjects,
      ...project.subject_headings,
    ], query))
    && (!filters.department
      || project.department === filters.department
      || project.departments_full.includes(filters.department))
    && (!filters.programType || project.program_type === filters.programType)
    && (!filters.campus || project.project_details.campus === filters.campus)
    && (!filters.subject
      || project.subjects.includes(filters.subject)
      || project.subject_headings.includes(filters.subject))
    && (!filters.paidOnly || compensationSignals(item.compensation).paid)
    && (!filters.creditOnly || compensationSignals(item.compensation).credit)
    && (filters.compensation === null || filters.compensation === hasCompensation)
  );
}

function matchesNSFFilters(item: DirectoryOpportunity, filters: DirectoryFilters) {
  const award = item.award!;
  const query = filters.searchQuery.trim().toLocaleLowerCase();
  return (
    (award.activeAwd || filters.includeConcluded)
    && (!query || includesQuery([
      award.title,
      award.awardeeName,
      award.pdPIName,
      award.program,
      award.fundProgramName,
    ], query))
  );
}

function matchesREUSiteFilters(item: DirectoryOpportunity, filters: DirectoryFilters) {
  const site = item.site!;
  const query = filters.searchQuery.trim().toLocaleLowerCase();
  return !query || includesQuery([
    site.title,
    site.institution,
    site.institution_city,
    site.site_location,
    site.institution_department,
    site.research_areas,
    site.research_topics_keywords,
  ], query);
}

export function filterAndSortOpportunities(
  items: DirectoryOpportunity[],
  filters: DirectoryFilters,
): DirectoryOpportunity[] {
  return items
    .filter((item) => {
      if (filters.sourceTab && item.kind !== filters.sourceTab) return false;
      if (item.kind === 'nsf-award') return matchesNSFFilters(item, filters);
      if (item.kind === 'reu-site') return matchesREUSiteFilters(item, filters);
      return matchesRutgersFilters(item, filters);
    })
    .sort((a, b) => {
      if (filters.sortBy === 'relevance') {
        const query = filters.searchQuery.trim().toLowerCase();
        const score = (item: DirectoryOpportunity) => (item.kind === 'rutgers' ? 100 : 0)
          + (query && item.title.toLowerCase().includes(query) ? 20 : 0)
          + (query && item.professor.toLowerCase().includes(query) ? 10 : 0);
        return score(b) - score(a) || a.title.localeCompare(b.title);
      }
      return a[filters.sortBy].localeCompare(b[filters.sortBy]) * (filters.sortOrder === 'asc' ? 1 : -1);
    });
}

export function createDirectoryViewModel(
  input: Omit<
    ResearchOpportunitiesDirectoryViewModel,
    'allResults' | 'results' | 'totalResults' | 'totalPages' | 'metrics' | 'filterOptions'
  > & {
    data: ResearchData | null;
    awards: NSFAward[];
    sites: REUSite[];
  },
): ResearchOpportunitiesDirectoryViewModel {
  const all = toOpportunities(input.data, input.awards, input.sites, input.filters);
  const filtered = filterAndSortOpportunities(all, input.filters);
  const pageSize = input.filters.sourceTab ? DIRECTORY_PAGE_SIZE : ITEMS_PER_PAGE;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const page = Math.min(input.filters.page, totalPages);
  const rutgersProjects = input.data?.projects ?? [];
  const filterOptions = {
    departments: [...new Set(rutgersProjects.flatMap((project) => (
      [project.department, ...project.departments_full].filter(Boolean)
    )))].sort(),
    programTypes: [...new Set(rutgersProjects.map((project) => project.program_type).filter(Boolean))].sort(),
    campuses: [...new Set(
      rutgersProjects.map((project) => project.project_details.campus).filter(Boolean) as string[],
    )].sort(),
    subjects: [...new Set(rutgersProjects.flatMap((project) => (
      [...project.subjects, ...project.subject_headings].filter(Boolean)
    )))].sort(),
  };

  return {
    ...input,
    filters: { ...input.filters, page },
    filterOptions,
    allResults: filtered,
    results: filtered.slice(input.filters.sourceTab ? 0 : (page - 1) * pageSize, page * pageSize),
    sourceCounts: { rutgers: rutgersProjects.filter(project => !project.is_concluded && (input.filters.includeREU || !isRutgersREU(project))).length, 'nsf-award': input.awards.filter(award => award.activeAwd).length, 'reu-site': input.sites.length },
    totalResults: filtered.length,
    totalPages,
    metrics: [
      {
        label: 'Rutgers projects',
        value: input.data?.metadata.total_projects ?? 0,
        detail: 'Checked-in snapshots',
      },
      { label: 'Visible results', value: filtered.length, detail: 'After filters' },
      {
        label: 'Optional sources',
        value: `${input.filters.includeNSF ? 'NSF ' : ''}${input.filters.includeREUSites ? 'REU' : ''}`.trim() || 'None',
        detail: 'Loaded on request',
      },
    ],
  };
}
